import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { taskTypeService } from "../services/task-type.service";
import type {
	CreateTaskTypeRequest,
	ErrorResponse,
	ListTaskTypesParams,
	UpdateTaskTypeRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const taskTypeKeys = {
	all: ["task-types"] as const,
	lists: () => [...taskTypeKeys.all, "list"] as const,
	list: (params?: ListTaskTypesParams) => [...taskTypeKeys.lists(), params] as const,
	details: () => [...taskTypeKeys.all, "detail"] as const,
	detail: (id: string) => [...taskTypeKeys.details(), id] as const,
};

// ─── useTaskTypes (list) ────────────────────────────────────────────
export function useTaskTypes(params?: ListTaskTypesParams) {
	return useQuery({
		queryKey: taskTypeKeys.list(params),
		queryFn: () => taskTypeService.list(params),
	});
}

// ─── useTaskType (getById) ───────────────────────────────────────────
export function useTaskType(id: string) {
	return useQuery({
		queryKey: taskTypeKeys.detail(id),
		queryFn: () => taskTypeService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateTaskType ──────────────────────────────────────────────
export function useCreateTaskType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateTaskTypeRequest) => taskTypeService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: taskTypeKeys.lists() });
			message.success("Topshiriq turi muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Topshiriq turi yaratishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateTaskType ──────────────────────────────────────────────
export function useUpdateTaskType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateTaskTypeRequest }) =>
			taskTypeService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: taskTypeKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: taskTypeKeys.detail(variables.id),
			});
			message.success("Topshiriq turi muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Topshiriq turi yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteTaskType ──────────────────────────────────────────────
export function useDeleteTaskType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => taskTypeService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: taskTypeKeys.lists() });
			message.success("Topshiriq turi muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Topshiriq turi o'chirishda xatolik";
			message.error(msg);
		},
	});
}
